import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BreadCrumb } from '@nationwide/internet-servicing-angular-components';
import { TellUsMoreModalComponent } from 'app/claimsDashboard/claim-tracker-property/tell-us-more-modal/tell-us-more-modal.component';
import { CACHE_EXPIRATION_TIME, PersonalConstants, propertyLossCauses } from 'app/common/personalConstants';
import { ROUTING } from 'app/common/routeConstants';
import { ClaimsHelperService } from 'app/shared/services/claims-helper.service';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';
import { environment } from 'environments/environment';

const CONTACT_ROLES = {
    COVERED_PARTY: 'coveredparty',
    INSURED: 'insured',
    INSURED_REP: 'insuredrep',
    INCIDENT_OWNER: 'incidentowner'
} as const;

const DEFAULT_CONTACT_ID = 'no-contact-id';

@Component({
    selector: 'is-claim-tracker-property',
    templateUrl: './claim-tracker-property.component.html',
    styleUrls: ['./claim-tracker-property.component.scss'],
    standalone: false
})
export class ClaimTrackerProperty implements OnInit {
    @ViewChild(TellUsMoreModalComponent) tellUsMoreModal: TellUsMoreModalComponent;
    @ViewChild('tabset') tabset!: ElementRef;
    @Input() useCaching!: boolean;
    breadCrumbs: BreadCrumb[];
    environment = environment;
    homeIconLink = environment.NATIONWIDE.REGISTERED_LOGO_URL;
    selectedTabIndex = 0;

    // Claim data
    claimNumber = '';
    claimStatus = '';
    claimOverviewError = false;
    contactPublicId: string = DEFAULT_CONTACT_ID;
    incidentDate = '';
    lossCause = '';
    lossLocation = '';
    policyNumber = '';
    isHideContractor = false;
    isPaymentsNotApplicable = false;
    isTimelineVisible = false;
    isUnregisteredUser: boolean = false;

    // UI state
    currentPage = 'Claim overview';
    fromMFA = '';
    isLoadingOverview = false;

    constructor(
        private readonly claimsTrackerService: ClaimsTrackerService,
        private readonly router: Router,
        private readonly logger: LoggerService,
        private readonly sessionService: SessionService,
        private readonly claimsHelperService: ClaimsHelperService,
        private utils: UtilService
    ) {}

    ngOnInit(): void {
        this.isUnregisteredUser = this.sessionService.getUserType() && this.sessionService.getUserType().indexOf('unregistered') >= 0;
        this.initializeSessionData();
        this.setBreadCrumbs();

        if (this.claimNumber) {
            this.initializeLoggerAndFetchClaimDetails();
        } else {
            this.logger.info('Property Claim Tracker: Claim number not found');
        }
        const tab = this.sessionService.getSessionItemDecrypted('tab');
        if (JSON.stringify(tab) !== '{}') {
            this.changeTab(tab);
            this.sessionService.setSessionItem('tab', null);
        }
    }

    private initializeSessionData(): void {
        this.fromMFA = this.sessionService.getSessionItemDecrypted('fromMFA') || '';
        this.policyNumber = this.sessionService.getPolicyNumber() || '';
        this.claimNumber = this.claimsHelperService.getClaimId();
        window.sessionStorage.setItem('dontShowPopup', 'true');
    }

    private initializeLoggerAndFetchClaimDetails(): void {
        this.logger.getStartLoadTimer();
        this.configureLogger();
        this.fetchClaimData();
        this.logPageLoadTime();
    }

    private configureLogger(): void {
        this.logger.setMember(this.sessionService.getUserType());
        this.logger.setComponentName('Property Tracker');
        this.logger.setClaimNumber(this.claimNumber);
        this.logger.setPageName('propertyTracker');
        this.logger.setPolicyNumber(this.policyNumber);
        this.logger.setProductType('Property');
    }

    private fetchClaimData(): void {
        this.getClaimContactId(this.claimNumber);
        this.getClaimDetails();
    }

    private logPageLoadTime(): void {
        const loadTime = this.logger.getStopLoadTimer();
        this.logger.info(`Property Claim Tracker Load Time`, loadTime);
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadHandler(_event: any): void {
        this.logger.logCurrentContents();
    }

    getClaimContactId(claimNumber: string): void {
        const CLAIM_CONTACT_CACHE_DATA = this.utils.getWithExpiry('CLAIM_CONTACT_DATA') || undefined;

        if (
            typeof CLAIM_CONTACT_CACHE_DATA === 'undefined' ||
            CLAIM_CONTACT_CACHE_DATA.id !== this.claimNumber
        ) {
            this.claimsTrackerService.getClaimsContacts(claimNumber)
            .subscribe({
                next: (res) => {
                    const validContact = this.findValidContact(res.claimContacts);
                    this.contactPublicId = validContact?.contactDTO?.publicID || DEFAULT_CONTACT_ID;
                    window.sessionStorage.setItem('claimsContactPublicId', this.contactPublicId);
                    this.utils.setWithExpiry('CLAIM_CONTACT_DATA', res, CACHE_EXPIRATION_TIME, this.claimNumber);
                },
                error: (error: any) => {
                    this.contactPublicId = DEFAULT_CONTACT_ID;
                    this.logger.error(`Error fetching claim contacts: ${error?.message || error}`);
                }
            });
        } else {
            const response = CLAIM_CONTACT_CACHE_DATA.value;
            const validContact = this.findValidContact(response.claimContacts);
            this.contactPublicId = validContact?.contactDTO?.publicID || DEFAULT_CONTACT_ID;
            window.sessionStorage.setItem('claimsContactPublicId', this.contactPublicId);
        }
    }

    private findValidContact(contacts: any[]): any {
        return contacts?.find((contact) =>
            this.hasRequiredPrimaryRole(contact.roles) &&
            this.hasIncidentOwnerRole(contact.roles)
        );
    }


    private hasRequiredPrimaryRole(roles: any[]): boolean {
        return roles?.some((roleObj) =>
            roleObj.role === CONTACT_ROLES.COVERED_PARTY ||
            roleObj.role === CONTACT_ROLES.INSURED ||
            roleObj.role === CONTACT_ROLES.INSURED_REP
        ) ?? false;
    }

    private hasIncidentOwnerRole(roles: any[]): boolean {
        return roles?.some((roleObj) => roleObj.role === CONTACT_ROLES.INCIDENT_OWNER) ?? false;
    }

    setBreadCrumbs(): void {
        const isMFAFlow = this.fromMFA === 'true';
        if (this.isUnregisteredUser) {
            this.homeIconLink = environment.NATIONWIDE.HOME;
        }
        if (isMFAFlow) {
            this.setupMFABreadcrumbs();
        } else {
            this.setupStandardBreadcrumbs();
        }
    }

    private setupMFABreadcrumbs(): void {
        this.homeIconLink = `${ROUTING.PROPERTY.BASE_PATH}${ROUTING.PROPERTY.CHILD_PATH_TRACKER}`;
        this.breadCrumbs = [];
    }

    private setupStandardBreadcrumbs(): void {
        sessionStorage.setItem('claimType', 'Property');
        this.breadCrumbs = [{
            name: 'Claims',
            link: `/fileclaim/info${PersonalConstants.CLAIMS_URLS.PROPERTY_CLAIM_LIST_PAGE}`
        }];
    }

    getClaimDetails(): void {
        this.isLoadingOverview = true;
        this.claimOverviewError = false;

        const CLAIM_DETAIL_CACHE_DATA = this.utils.getWithExpiry('CLAIM_DETAIL_DATA') || undefined;

        if (
            typeof CLAIM_DETAIL_CACHE_DATA === 'undefined' ||
            CLAIM_DETAIL_CACHE_DATA.id !== this.claimNumber
        ) {
            this.claimsTrackerService.getClaimDetails(this.claimNumber).subscribe({
                next: (response) => {
                    this.populateClaimData(response);
                    this.utils.setWithExpiry(
                        'CLAIM_DETAIL_DATA',
                        response,
                        CACHE_EXPIRATION_TIME,
                        this.claimNumber
                      );
                    this.isLoadingOverview = false;
                },
                error: (error) => {
                    this.handleClaimDetailsError(error);
                }
            });
        } else {
            const response = CLAIM_DETAIL_CACHE_DATA.value;
            this.populateClaimData(response);
            this.isLoadingOverview = false;
        }
    }

    private populateClaimData(response: any): void {
        this.lossCause = propertyLossCauses[response.lossCause] || response.lossCause;
        this.claimStatus = response.claimState || '';
        this.incidentDate = response.lossDate || '';
        this.sessionService.setSessionItem('incidentDate', this.incidentDate);
        this.lossLocation = this.formatLossLocation(response.lossLocation?.displayName || '');
        if (response && response.closedOutcome) {
            window.sessionStorage.setItem('hasClosedOutcome', 'true');
            window.sessionStorage.setItem('closedOutcome', response.closedOutcome);
        }
    }

    private formatLossLocation(displayName: string): string {
        if (!displayName) {
            return '';
        }

        // Remove 4-digit zip code extension (e.g., "12345-6789" becomes "12345")
        const formatted = displayName.replace(/(\d{5})-\d{4}/g, '$1');

        // Split by comma to handle city, state, zip separately
        const parts = formatted.split(',').map((part) => part.trim());

        if (parts.length === 0) {
            return formatted;
        }

        // Process each part
        const processedParts = parts.map((part, partIndex) => {
            const words = part.split(/\s+/);

            return words.map((word, wordIndex) => {
                // Check if this is a 5-digit zip code (should remain as-is)
                if (/^\d{5}$/.test(word)) {
                    return word;
                }

                // Check if this is a Canadian postal code (6 alphanumeric, should be UPPERCASE)
                // Format: A1A 1A1 or A1A1A1
                if (/^[a-zA-Z]\d[a-zA-Z]\s?\d[a-zA-Z]\d$/i.test(word)) {
                    return word.toUpperCase().replace(/\s/g, ' '); // Preserve space if exists
                }

                // Check if this is part of a Canadian postal code split across words
                const isFirstPartOfPostalCode = wordIndex < words.length - 1 &&
                    /^[a-zA-Z]\d[a-zA-Z]$/i.test(word) &&
                    /^\d[a-zA-Z]\d$/i.test(words[wordIndex + 1]);

                const isSecondPartOfPostalCode = wordIndex > 0 &&
                    /^\d[a-zA-Z]\d$/i.test(word) &&
                    /^[a-zA-Z]\d[a-zA-Z]$/i.test(words[wordIndex - 1]);

                if (isFirstPartOfPostalCode || isSecondPartOfPostalCode) {
                    return word.toUpperCase();
                }

                // State/province codes only appear in parts after the first comma (partIndex >= 1)
                // and they are 2-letter codes followed by zip/postal code or at end
                if (partIndex >= 1 && /^[a-zA-Z]{2}$/.test(word)) {
                    const isLastWord = wordIndex === words.length - 1;
                    const nextWordIsZipOrPostalCode = wordIndex < words.length - 1 && (
                        /^\d{5}$/.test(words[wordIndex + 1]) || // US zip
                        /^[a-zA-Z]\d[a-zA-Z]$/i.test(words[wordIndex + 1]) || // Canadian postal code first part
                        /^[a-zA-Z]\d[a-zA-Z]\d[a-zA-Z]\d$/i.test(words[wordIndex + 1]) // Canadian postal code full
                    );

                    if (isLastWord || nextWordIsZipOrPostalCode) {
                        return word.toUpperCase();
                    }
                }


                // Otherwise, apply title case (city names, street names, etc.)
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            }).join(' ');
        });

        return processedParts.join(', ');
    }

    private handleClaimDetailsError(error: any): void {
        this.isLoadingOverview = false;
        this.claimOverviewError = true;
        this.logger.error(`Error fetching claim details: ${error?.message || 'Unknown error'}`);
    }


    openTellUsMore(): void {
        this.logger.info('Property Claim Tracker - Tell us more modal opened');

        if (this.tellUsMoreModal) {
            this.tellUsMoreModal.open();
        } else {
            this.logger.error('TellUsMoreModalComponent is not initialized.');
        }
    }

    onTabChange(event: any): void {
        const selectedTab = event?.detail?.tab?.textContent;

        if (!selectedTab) {
            this.logger.error('Property Claim Tracker - Invalid tab selection event');
            return;
        }
        this.changeTab(selectedTab);

        this.logger.info(`Property Claim Tracker - tab selected: ${selectedTab}`);
    }

    changeTab(selectedTab: string): void {
        const el = this.tabset?.nativeElement;
        if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
        let temp = 0;
        if (this.isHideContractor) {
            temp = 1;
        }
        switch (selectedTab) {
            case 'Updates':
                this.selectedTabIndex = 0;
                break;
            case 'Documents':
                this.selectedTabIndex = 1;
                break;
            case 'Coverages':
                this.selectedTabIndex = 2;
                break;
            case 'Contractors':
                this.selectedTabIndex = 3;
                break;
            case 'Payments':
                this.selectedTabIndex = 4 - temp;
                break;
            case 'Support':
                this.selectedTabIndex = 5 - temp;
                break;
            default:
                this.logger.error(`Invalid tab selected: ${selectedTab}`);
                break;
        }
    }

    onTileClick(event: any): void {
        const selectedTile = event?.target?.label;

        if (!selectedTile) {
            this.logger.error('Property Claim Tracker - Invalid tile selection event');
            return;
        }

        this.logger.info(`Property Claim Tracker - tile clicked: ${selectedTile}`);
        this.navigateToTile(selectedTile);
    }

    private navigateToTile(tileName: string): void {
        const route = `/property/tracker/${tileName.toLowerCase()}`;
        this.router.navigate([route]);
    }

    hideContractor(flag: boolean): void {
        this.isHideContractor = flag;
    }

    setPaymentsNotApplicable(flag: boolean): void {
        this.isPaymentsNotApplicable = flag;
    }

    onComponentLoadTime(loadTime: number): void {
        this.logger.info(`Property Claim Tracker Load Time`, loadTime);
    }

    showTimeline(flag: boolean): void {
        this.isTimelineVisible = flag;
    }
}
